import React, { useState } from 'react'
import { Alert, Grid, CircularProgress } from '@mui/material'
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator'
import { useStyle } from './Style'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import ModalInc from '../../../../../shared-ui/ModalInc'
import AlertDialog from '../../../../../components/AlertDialog/index.es6'
import { hasAuthourity } from '../../../../../lib/access.es6'
import { reloadPage } from '../../../../../lib/utils.es6'
import SupportingDocumentUpload from './SupportingDocumentUpload'
import {
  SUPPORTING_DOCUMENT_LABELS,
  screeningFlagPrefix,
  screeningLabel,
  screeningReasons,
  findSupportingDocument
} from '../../lib/flaggedPolicyDecision'

// What the AML, PEP or IPRS screening actually found, shown both beside the
// stage and inside its decision modals. Without it the reviewer is asked to
// approve "PEP Approval" with the finding buried in the policy details tab.
const ScreeningReasons = props => {
  if (!props.reasons.length) {
    return null
  }
  // AML and PEP only ever screen the policy holder, so naming the subject
  // there would be noise. IPRS screens each person, and then it is the point.
  const named = props.reasons.length > 1
  return (
    <div className={props.className}>
      <span className={props.classes.screeningTitle}>
        {props.label} screening
      </span>
      {props.reasons.map(entry => (
        <div key={entry.subject} className={props.classes.screeningReason}>
          {named ? (
            <span className={props.classes.screeningSubject}>
              {entry.subject}:{' '}
            </span>
          ) : null}
          {entry.reason}
        </div>
      ))}
    </div>
  )
}

// One note-capture modal behind every stage action. They differ only in
// wording, in whether the note is mandatory, and in what submitting does.
// Screening stages (AML/PEP) additionally take a supporting document, and
// cannot be submitted without one.
const NoteModal = props => (
  <ModalInc
    modalTitle={props.title}
    subTitle={props.subTitle}
    open={props.open}
    close={props.onClose}
    fullWidth
  >
    <ValidatorForm onSubmit={() => props.onSubmit()}>
      <Grid container spacing={2}>
        {props.screeningReasons ? (
          <Grid item xs={12} sm={12} md={12}>
            {props.screeningReasons}
          </Grid>
        ) : null}
        <Grid item xs={12} sm={12} md={12}>
          <TextValidator
            name={props.name}
            id={props.name}
            label={props.label}
            value={props.value}
            onChange={event => props.onChange(event.target.value)}
            multiline
            rows={5}
            maxRows={4}
            fullWidth
            variant="outlined"
            validators={props.required ? ['required'] : []}
            errorMessages={props.required ? [props.requiredMessage] : []}
          />
        </Grid>
        {props.documentUpload ? (
          <Grid item xs={12} sm={12} md={12}>
            {props.documentUpload}
          </Grid>
        ) : null}
        {props.submitDisabled ? (
          <Grid item xs={12} sm={12} md={12}>
            <Alert severity="warning">
              This stage cannot be decided until a supporting document is
              attached.
            </Alert>
          </Grid>
        ) : null}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}></Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8} md={8}>
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={!!props.submitDisabled}
                style={{ marginTop: '20px', float: 'right' }}
              >
                {props.submitText}
              </ButtonInc>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                style={{ marginTop: '20px' }}
                onClick={props.onClose}
              >
                Go back
              </ButtonInc>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ValidatorForm>
  </ModalInc>
)

// Multi-level (maker-checker) approval control. Shows the name of the single
// lowest-sequence Pending stage with inline Approve/Reject actions. The server
// owns separation of duties, distinct-actor-per-stage and sequencing; any
// violation comes back as a 4xx and is surfaced here. A stage rejection moves
// the policy to Rejected, after which the existing Re-Confirm / Discard branch
// takes over.
//
// Partners that opt in (allowRequestChanges) get a third option between those
// two: send the policy back for changes. That decides nothing — the stage
// keeps its Pending check — it only flags the policy need_action, which moves
// it out of this reviewer's queue and into the Need Action tab. While it is
// flagged the decision buttons are replaced by "Send Back for Approval", so
// the policy is unambiguously the other side's to act on; returning it clears
// the flag and puts it in the Corrected policies tab of the stage that asked.
const MakerChecks = props => {
  const classes = useStyle()

  const [openApproveDialog, setOpenApproveDialog] = useState(false)
  const [openRejectDialog, setOpenRejectDialog] = useState(false)
  const [openChangesDialog, setOpenChangesDialog] = useState(false)
  const [openSendBackDialog, setOpenSendBackDialog] = useState(false)
  const [reason, setReason] = useState('')
  const [approveNote, setApproveNote] = useState('')
  const [changesNotes, setChangesNotes] = useState('')
  const [sendBackNotes, setSendBackNotes] = useState('')
  const [uploadedFile, setUploadedFile] = useState(null)

  const checks = (props.makerChecks || [])
    .slice()
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))

  if (!checks.length) {
    return ''
  }

  // The only actionable stage is the lowest-sequence check still Pending.
  let currentCheck = null
  for (let i = 0; i < checks.length; i++) {
    if (checks[i].status === 'Pending') {
      currentCheck = checks[i]
      break
    }
  }

  if (!currentCheck) {
    return ''
  }

  // On partners in DASHBOARD_ONLY_DECISION_PARTNERS (see PolicyHeader.js), the
  // whole maker-checks stage is withheld when the operator didn't arrive from
  // the review queue — props.dashboardGatePasses is already resolved there
  // and just forwarded (defaulting to allowed when the parent didn't set it).
  const passesDashboardGate =
    !props.requireDashboardOrigin || !!props.dashboardGatePasses
  if (!passesDashboardGate) {
    return ''
  }

  const canDecide = hasAuthourity('APPROVE_POLICY')

  // An AML or PEP stage is the compliance decision on a flagged policy, so it
  // is held to a stricter bar than the other stages: a reason on approval as
  // well as rejection, and supporting evidence on file either way. A document
  // attached on an earlier visit counts, so the reviewer is not asked twice.
  const flagPrefix = screeningFlagPrefix(currentCheck)
  const supportingDocumentLabel = flagPrefix
    ? SUPPORTING_DOCUMENT_LABELS[flagPrefix]
    : null
  const existingDocument = flagPrefix
    ? findSupportingDocument(props.policy, flagPrefix)
    : null
  const hasSupportingDocument = !!uploadedFile || !!existingDocument
  // Only gate stages that can actually take a document — without a policy guid
  // or an upload handler there would be no way to satisfy the requirement.
  const requiresEvidence =
    !!flagPrefix && !!props.policyGuid && !!props.uploadDocument

  const stageScreeningLabel = screeningLabel(currentCheck)
  const stageReasons = screeningReasons(currentCheck, props.policy)

  const renderScreeningReasons = () => (
    <ScreeningReasons
      label={stageScreeningLabel}
      reasons={stageReasons}
      classes={classes}
      className={classes.screeningPanel}
    />
  )

  const renderDocumentUpload = () =>
    requiresEvidence ? (
      <SupportingDocumentUpload
        policyGuid={props.policyGuid}
        label={supportingDocumentLabel}
        uploadDocument={props.uploadDocument}
        existingDocument={existingDocument}
        uploadedFile={uploadedFile}
        satisfied={hasSupportingDocument}
        onUploaded={setUploadedFile}
      />
    ) : null

  // Sending a policy back for changes belongs to the level below, whose users
  // capture policies. Approvers are allowed it too: on a partner where the same
  // account both captures and approves at the lower level, gating on
  // CAPTURE_POLICY alone would strand a flagged policy with nobody able to
  // return it.
  const canSendBack =
    hasAuthourity('CAPTURE_POLICY') || hasAuthourity('APPROVE_POLICY')

  // A returned policy is out of this stage's hands until it comes back, so the
  // stage decisions are withheld while the flag is set.
  const returnedForChanges = !!props.needAction
  const canRequestChanges =
    !!props.allowRequestChanges && canDecide && !returnedForChanges

  const submitRequestChanges = () => {
    setOpenChangesDialog(false)
    props.requestChanges(changesNotes)
    setChangesNotes('')
  }

  const submitSendBack = () => {
    setOpenSendBackDialog(false)
    props.sendBackForApproval(sendBackNotes)
    setSendBackNotes('')
  }

  // A screening stage needs both a reason and evidence; the form enforces it,
  // these are the backstops.
  const evidenceMissing = reasonGiven =>
    requiresEvidence && (!reasonGiven.trim() || !hasSupportingDocument)

  const submitApprove = () => {
    if (evidenceMissing(approveNote)) return
    setOpenApproveDialog(false)
    const decision = {
      decision: 'Approved',
      check_guid: currentCheck.guid
    }
    // The note is optional outside the screening stages — only sent (and
    // persisted to policy notes) when provided.
    if (approveNote && approveNote.trim()) {
      decision.reason = approveNote.trim()
    }
    props.decideMakerCheck(decision)
  }

  const submitReject = () => {
    if (evidenceMissing(reason)) return
    setOpenRejectDialog(false)
    props.decideMakerCheck({
      decision: 'Rejected',
      reason,
      check_guid: currentCheck.guid
    })
  }

  // Show the human stage label ("AML Check", "Quality Check Approval") rather
  // than the raw approval type ("AgentApproval") the server names in its
  // separation-of-duties / sequencing errors.
  const humanizeStage = message => {
    if (!message || !currentCheck.type || !currentCheck.label) return message
    return message.split(currentCheck.type).join(currentCheck.label)
  }

  const readError = error => {
    if (!error) return ''
    let message
    if (typeof error === 'string') message = error
    else if (Array.isArray(error.message)) message = error.message.join(', ')
    else if (error.message) message = error.message
    else return 'An unexpected error occurred'
    return humanizeStage(message)
  }

  const successMessage = () => {
    const result = props.decisionResult || {}
    if (result.decision === 'Rejected') {
      return `Policy ${props.policyNumber} was rejected at this stage and moved to Rejected.`
    }
    if (result.activated) {
      return `All approval stages complete. Policy ${props.policyNumber} is now activated.`
    }
    return `Stage approved. Policy ${props.policyNumber} now awaits the next approval stage.`
  }

  // The handoff is not a stage decision, so it needs its own copy. Naming the
  // direction matters: "sent back" and "returned for approval" are opposite
  // moves and the checker needs to know which one just happened.
  const sendingBack = props.needActionDirection === 'requestChanges'

  const needActionProgress = sendingBack
    ? `Sending policy ${props.policyNumber} back for changes...`
    : `Returning policy ${props.policyNumber} for approval...`

  const needActionSuccessMessage = sendingBack
    ? `Policy ${props.policyNumber} was sent back for changes and now sits in the Need Action queue.`
    : `Policy ${props.policyNumber} was returned and now awaits approval at the same stage.`

  const needActionErrorTitle = sendingBack
    ? `Could not send policy ${props.policyNumber} back for changes`
    : `Could not return policy ${props.policyNumber} for approval`

  return (
    <div className={classes.wrapper}>
      <span className={classes.stageName}>
        {returnedForChanges
          ? `${currentCheck.label || currentCheck.type} — sent back for changes`
          : currentCheck.label || currentCheck.type}
      </span>
      {returnedForChanges ? (
        canSendBack ? (
          <div className={classes.actions}>
            <ButtonInc
              style={{ background: '#008000' }}
              onClick={() => setOpenSendBackDialog(true)}
            >
              Send Back for Approval
            </ButtonInc>
          </div>
        ) : (
          <span className={classes.noPerm}>
            This policy was sent back for changes. See the policy notes for what
            was asked for.
          </span>
        )
      ) : canDecide ? (
        <div className={classes.actions}>
          <ButtonInc onClick={() => setOpenApproveDialog(true)}>
            Approve
          </ButtonInc>
          {canRequestChanges ? (
            <ButtonInc
              style={{ background: '#1976d2' }}
              onClick={() => setOpenChangesDialog(true)}
            >
              Request Changes
            </ButtonInc>
          ) : null}
          <ButtonInc
            style={{ background: '#8F191C' }}
            onClick={() => setOpenRejectDialog(true)}
          >
            Reject
          </ButtonInc>
        </div>
      ) : (
        <span className={classes.noPerm}>
          You do not have permission to decide this stage.
        </span>
      )}

      <ScreeningReasons
        label={stageScreeningLabel}
        reasons={stageReasons}
        classes={classes}
        className={`${classes.screeningPanel} ${classes.screeningPanelInline}`}
      />

      <NoteModal
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
        title={`Approve this stage for policy ${props.policyNumber}?`}
        subTitle={
          requiresEvidence
            ? 'A reason and a supporting document are required to approve this stage.'
            : 'You may add an optional note.'
        }
        name="approveNote"
        label={
          requiresEvidence ? 'Enter reason for approving' : 'Note (optional)'
        }
        value={approveNote}
        onChange={setApproveNote}
        onSubmit={submitApprove}
        submitText="Approve Stage"
        required={requiresEvidence}
        requiredMessage="Please enter a reason for approving this stage"
        documentUpload={renderDocumentUpload()}
        screeningReasons={renderScreeningReasons()}
        submitDisabled={requiresEvidence && !hasSupportingDocument}
      />

      <NoteModal
        open={openChangesDialog}
        onClose={() => setOpenChangesDialog(false)}
        title="Send this policy back for changes"
        subTitle={
          'The policy stays at this stage but moves to the Need Action queue ' +
          'until the changes are made and it is sent back.'
        }
        name="changesNotes"
        label="Enter the changes required"
        value={changesNotes}
        onChange={setChangesNotes}
        onSubmit={submitRequestChanges}
        submitText="Request Changes"
        required
        requiredMessage="Please describe the changes required"
      />

      <NoteModal
        open={openSendBackDialog}
        onClose={() => setOpenSendBackDialog(false)}
        title={`Send policy ${props.policyNumber} back for approval`}
        subTitle="It returns to the approval queue it was sent back from."
        name="sendBackNotes"
        label="Note on what was changed (optional)"
        value={sendBackNotes}
        onChange={setSendBackNotes}
        onSubmit={submitSendBack}
        submitText="Send Back for Approval"
      />

      <AlertDialog
        custom
        show={props.loader}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Recording decision for policy {props.policyNumber}...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.success}
        size="sm"
        title={successMessage()}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        showCancel={false}
        showConfirm={false}
        cancelBtnText="Cancel"
        confirmBtnCssClass
      >
        <div>
          <ButtonInc
            variant="contained"
            onClick={() => reloadPage()}
            style={{ background: 'green' }}
          >
            Ok
          </ButtonInc>
        </div>
      </AlertDialog>

      <AlertDialog
        show={!!props.error}
        danger
        title={`Could not record the decision for policy ${props.policyNumber}`}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {readError(props.error)}
      </AlertDialog>

      {/* The handoff gets its own progress, success and failure surface. It used
          to ride on the generic "Policy updated successfully" dialog, which
          said nothing about which way the policy went — and showed nothing at
          all when the write failed, leaving the flag silently unchanged. */}
      <AlertDialog
        custom
        show={!!props.needActionLoader}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>{needActionProgress}</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={!!props.needActionSuccess}
        size="sm"
        title={needActionSuccessMessage}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        showCancel={false}
        showConfirm={false}
        cancelBtnText="Cancel"
        confirmBtnCssClass
      >
        <div>
          <ButtonInc
            variant="contained"
            onClick={() => reloadPage()}
            style={{ background: 'green' }}
          >
            Ok
          </ButtonInc>
        </div>
      </AlertDialog>

      <AlertDialog
        show={!!props.needActionError}
        danger
        title={needActionErrorTitle}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {readError(props.needActionError)}
      </AlertDialog>

      <NoteModal
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        title="Reject this approval stage"
        subTitle={
          requiresEvidence
            ? 'A reason and a supporting document are required to reject this stage.'
            : 'A reason is required when rejecting a stage.'
        }
        name="reason"
        label="Enter reason for rejecting this stage"
        value={reason}
        onChange={setReason}
        onSubmit={submitReject}
        submitText="Reject Stage"
        required
        requiredMessage="Please enter a reason for rejecting this stage"
        documentUpload={renderDocumentUpload()}
        screeningReasons={renderScreeningReasons()}
        submitDisabled={requiresEvidence && !hasSupportingDocument}
      />
    </div>
  )
}

export default MakerChecks
